import { useState } from 'react';

const VIEW_WIDTH = 640;
const VIEW_HEIGHT = 220;
const MARGIN = { top: 12, right: 16, bottom: 32, left: 40 };

export function integerYMax(values) {
  const finite = (values || []).filter((value) => Number.isFinite(value));
  const max = Math.max(0, ...finite);
  if (max <= 1) return 1;
  if (max <= 5) return 5;
  const mag = 10 ** Math.floor(Math.log10(max));
  return Math.ceil(max / mag) * mag;
}

export function yTickValues(max, count = 4) {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  if (max <= 1) return [0, 1];
  if (Number.isInteger(max) && max <= 5) {
    return [...Array(max + 1).keys()];
  }
  const steps = Math.max(1, count);
  const ticks = [];
  for (let i = 0; i <= steps; i += 1) {
    const value = (max * i) / steps;
    ticks.push(Number.isInteger(max) ? Math.round(value) : value);
  }
  return [...new Set(ticks)];
}

export function pickXTickIndexes(length) {
  if (length <= 0) return [];
  if (length <= 8) return [...Array(length).keys()];
  const step = Math.ceil((length - 1) / 5);
  const ticks = [0];
  for (let i = step; i < length - 1; i += step) ticks.push(i);
  ticks.push(length - 1);
  return [...new Set(ticks)];
}

export function formatChartDate(value) {
  if (value == null) return '';
  const day = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    const [, month, date] = day.split('-');
    return `${Number(month)}/${Number(date)}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return `${parsed.getUTCMonth() + 1}/${parsed.getUTCDate()}`;
}

function pointDate(point, dateField) {
  if (!point) return '';
  return point[dateField] ?? point.date ?? point.asOf ?? '';
}

function plotMetrics(length) {
  const innerWidth = VIEW_WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = VIEW_HEIGHT - MARGIN.top - MARGIN.bottom;
  const slot = length > 0 ? innerWidth / length : innerWidth;
  return { innerWidth, innerHeight, slot };
}

export function nearestSeriesIndex(x, centers) {
  let best = 0;
  let bestDist = Infinity;
  centers.forEach((center, index) => {
    const distance = Math.abs(center - x);
    if (distance < bestDist) {
      best = index;
      bestDist = distance;
    }
  });
  return best;
}

export default function TimeSeriesChart({
  series,
  field,
  dateField = 'date',
  mode = 'bar',
  formatY,
  emptyMessage,
  className = 'time-series-chart',
  ariaLabel,
  renderTooltip,
}) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const points = series || [];
  const values = points.map((point) => {
    const raw = point?.[field];
    return Number.isFinite(raw) ? raw : 0;
  });

  if (points.length === 0) {
    return <p className="chart-empty">{emptyMessage || 'No series data in range.'}</p>;
  }

  const yMax = integerYMax(values);
  const yTicks = yTickValues(yMax);
  const formatTick = formatY || ((value) => String(Math.round(value)));
  const { innerHeight, slot } = plotMetrics(points.length);
  const barWidth = Math.max(2, Math.min(16, slot * 0.62));
  const xTicks = pickXTickIndexes(points.length);
  const peakIndex = values.reduce((best, value, index) => (value > values[best] ? index : best), 0);
  const peakDate = formatChartDate(pointDate(points[peakIndex], dateField));
  const label =
    ariaLabel ||
    `${field} over ${points.length} points. Peak ${formatTick(values[peakIndex])}${
      peakDate ? ` on ${peakDate}` : ''
    }.`;

  const yFor = (value) => MARGIN.top + innerHeight * (1 - value / yMax);
  const xCenter = (index) => MARGIN.left + index * slot + slot / 2;
  const defaultTooltip = (index, value) =>
    `${formatChartDate(pointDate(points[index], dateField))}: ${formatTick(value)}`;

  const tooltipContent = (index, value) =>
    renderTooltip
      ? renderTooltip({
          point: points[index],
          value,
          index,
          date: pointDate(points[index], dateField),
        })
      : defaultTooltip(index, value);

  const linePoints = values
    .map((value, index) => `${xCenter(index).toFixed(2)},${yFor(value).toFixed(2)}`)
    .join(' ');
  const centers = points.map((_, index) => xCenter(index));

  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width) return;
    const x = ((event.clientX - rect.left) / rect.width) * VIEW_WIDTH;
    setHoverIndex(nearestSeriesIndex(x, centers));
  };

  return (
    <div className="chart-hover" onMouseLeave={() => setHoverIndex(null)}>
    <svg
      className={className}
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={label}
      onMouseMove={handleMove}
    >
      {yTicks.map((tick) => {
        const y = yFor(tick);
        return (
          <g key={`y-${tick}`}>
            <line
              className={tick === 0 ? 'time-series-baseline' : 'time-series-grid'}
              x1={MARGIN.left}
              x2={VIEW_WIDTH - MARGIN.right}
              y1={y}
              y2={y}
            />
            <text className="time-series-axis" x={MARGIN.left - 6} y={y + 4} textAnchor="end">
              {formatTick(tick)}
            </text>
          </g>
        );
      })}

      {xTicks.map((index) => (
        <text
          key={`x-${index}`}
          className="time-series-axis"
          x={xCenter(index)}
          y={VIEW_HEIGHT - 8}
          textAnchor="middle"
        >
          {formatChartDate(pointDate(points[index], dateField))}
        </text>
      ))}

      {mode === 'line' ? (
        <>
          {points.length > 1 && (
            <polyline className="time-series-line" points={linePoints} />
          )}
          {hoverIndex != null ? (
            <circle
              className="time-series-dot"
              cx={xCenter(hoverIndex)}
              cy={yFor(values[hoverIndex])}
              r="3"
            />
          ) : null}
        </>
      ) : (
        values.map((value, index) => {
          const height = Math.max(value > 0 ? 2 : 0, innerHeight * (value / yMax));
          const x = xCenter(index) - barWidth / 2;
          const y = yFor(value);
          return (
            <rect
              key={`bar-${index}`}
              className="time-series-bar"
              x={x}
              y={value > 0 ? y : yFor(0)}
              width={barWidth}
              height={height}
              fill="currentColor"
              rx="1"
            >
              <title>{defaultTooltip(index, value)}</title>
            </rect>
          );
        })
      )}
    </svg>
    {hoverIndex != null ? (
      <div
        className={`chart-hover-tip${renderTooltip ? ' chart-hover-tip-rich' : ''}`}
        style={{ left: `${(centers[hoverIndex] / VIEW_WIDTH) * 100}%` }}
      >
        {tooltipContent(hoverIndex, values[hoverIndex])}
      </div>
    ) : null}
    </div>
  );
}
